// frontend/src/components/dfs/ShareContestButton.tsx
//
// The Edge-1 share vehicle (STRATEGY H1/H3; BAR-FUNNEL step 10): a quiet
// native-share button that assembles the group-chat payload - short text plus
// an attributed link to the current weekend contest. The screen is the
// artifact; this button is the payload assembler. Falls back to copying the
// text+link where the Web Share API is unavailable (desktop browsers).
//
// Also exports InvitePrompt (BAR-FUNNEL step 10b): the ONE sanctioned share
// prompt. It occupies the mobile bottom slot (where Edit Lineup lives) once,
// right after lineup submission - the only window where a recipient can still
// join the SAME contest - and must be dismissed (honest X) or acted on before
// the slot reverts to Edit. 'Prompted' on the share event separates prompted
// from organic share conversion.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Share, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import { userShareRef } from '@/utils/referral';
import { webUrl } from '@/utils/links';
import { shareSheetAvailable, openShareSheet } from '@/services/share';

// Owner-refinable copy. Guardrails (STRATEGY M3 / BAR-FUNNEL): no "win money"
// where no prize exists, no marketing-speak; competitive and neutral.
const SHARE_TEXT = "Think you can beat my lineup? This weekend's contest:";
const INVITE_COPY = "It's more fun when you beat your friends";

type ShareOutcome = 'native' | 'clipboard' | 'prompt' | null;

function useContestShare(contestId: number | null | undefined, prompted: boolean, venueSlug?: string | null) {
    const { user } = useAuth();
    const [copied, setCopied] = useState(false);

    const handleShare = useCallback(async (): Promise<ShareOutcome> => {
        // /dfs/build/next is a forward-looking alias: weekday shares resolve
        // to the next buildable slate, while scorecard `today` remains
        // backward-looking for recent results.
        const ref = user?.id ? `?ref=${userShareRef(user.id)}` : '';
        // A partner bar's contest shares the bar's own page (its contest, its code).
        const url = venueSlug ? webUrl(`/${venueSlug}`) : webUrl(`/dfs/build/next${ref}`);
        let method: ShareOutcome = null;
        try {
            if (shareSheetAvailable()) {
                try {
                    const shared = await openShareSheet({ title: 'Fantasy Futbol', text: SHARE_TEXT, url });
                    // Dismissed sheet = no share happened - no event; only
                    // completed shares/copies count toward the share rate.
                    if (!shared) return null;
                    method = 'native';
                } catch {
                    // Share sheet unavailable after all - fall through to the
                    // clipboard path rather than a silent no-op.
                }
            }
            if (method !== 'native') {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                    method = 'clipboard';
                } else {
                    // No share API and no clipboard (http origin / old browser):
                    // last-resort prompt so the button is never a silent no-op.
                    method = 'prompt';
                    window.prompt('Copy this link:', url);
                }
            }
            mixpanelTrack('Contest Share Initiated', {
                'Contest ID': contestId ?? null,
                'Method': method,
                'Has Ref': !!user?.id,
                'Prompted': prompted,
                'Venue': venueSlug ?? null,
            });
            return method;
        } catch {
            // Clipboard denied - no event.
            return null;
        }
    }, [contestId, user?.id, prompted, venueSlug]);

    return { handleShare, copied };
}

interface ShareContestButtonProps {
    contestId?: number | null;
    venueSlug?: string | null;
}

const ShareContestButton: React.FC<ShareContestButtonProps> = ({ contestId, venueSlug }) => {
    const { handleShare, copied } = useContestShare(contestId, false, venueSlug);

    return (
        <button
            onClick={() => { void handleShare(); }}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-accent-light-blue active:text-accent-light-blue transition-colors"
            title="Share this weekend's contest"
        >
            <Share size={14} />
            <span>{copied ? 'Link copied' : 'Share'}</span>
        </button>
    );
};

interface InvitePromptProps {
    contestId?: number | null;
    venueSlug?: string | null;
    // Called on X or on a completed share - the bottom slot reverts to Edit.
    onDismiss: () => void;
}

export const InvitePrompt: React.FC<InvitePromptProps> = ({ contestId, venueSlug, onDismiss }) => {
    const { handleShare, copied } = useContestShare(contestId, true, venueSlug);
    const rootRef = useRef<HTMLDivElement>(null);

    // Track the impression honestly: (a) skip when CSS-hidden - the mobile
    // container is lg:hidden, so on desktop this component MOUNTS invisibly
    // (offsetParent is null inside display:none); (b) once per contest per
    // session - mobile tab switches remount the component.
    useEffect(() => {
        const el = rootRef.current;
        if (!el || el.offsetParent === null) return;
        const trackedKey = `sharePromptShownTracked:${contestId ?? 'na'}`;
        if (sessionStorage.getItem(trackedKey)) return;
        sessionStorage.setItem(trackedKey, '1');
        mixpanelTrack('Share Prompt Shown', { 'Contest ID': contestId ?? null });
    }, [contestId]);

    const onInvite = async () => {
        const outcome = await handleShare();
        if (outcome === 'native' || outcome === 'prompt') {
            onDismiss();
        } else if (outcome === 'clipboard') {
            // Leave "Link copied" visible for a beat before the slot reverts.
            setTimeout(onDismiss, 1500);
        }
        // null = share sheet dismissed: keep the prompt - retry or X out.
    };

    return (
        // Featured treatment: the same solid gold ring the contest cards use
        // for "look here", plus a one-time slide-up entrance (motion-safe:
        // respects prefers-reduced-motion). Louder within brand, not off it.
        <div ref={rootRef} className="relative bg-surface-card border border-gray-700/60 ring-2 ring-accent-yellow/60 rounded-lg p-3 motion-safe:animate-slide-up">
            <button
                onClick={() => {
                    mixpanelTrack('Share Prompt Dismissed', { 'Contest ID': contestId ?? null });
                    onDismiss();
                }}
                className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-200 active:text-gray-200 transition-colors"
                title="Dismiss"
                aria-label="Dismiss invite prompt"
            >
                <X size={18} />
            </button>
            {/* px-8 on BOTH sides (not pr-8): keeps centered text truly
                centered while clearing the absolute X; wraps, never truncates. */}
            <p className="text-sm font-heading font-bold uppercase text-gray-100 text-center px-8 mb-2">{INVITE_COPY}</p>
            <button
                onClick={() => { void onInvite(); }}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg font-heading font-bold text-lg text-center text-primary bg-accent-yellow hover:bg-yellow-500 active:bg-yellow-500 active:scale-[0.98] transition-colors shadow-lg"
            >
                <Share size={18} />
                <span>{copied ? 'Link copied' : 'Invite'}</span>
            </button>
        </div>
    );
};

export default ShareContestButton;
